// LRPO-5314 (QA re-open 2026-07-13, Viktor — "The data is not scalable and
// appears very small on a chart with a lot of data") — scales a Demographics
// bar chart's height to the number of categories it actually received.
//
// NOT the label-truncation defect the earlier passes of this ticket fixed (see
// chart_axis_label_crop.js — that crop is correct and untouched here). This is
// CHART HEIGHT: every demographics card renders its horizontal bar chart at a
// STATIC `height: '320px'` (exhibitor/reports/tabs/_demographics.html.erb).
// 320px is comfortable for 5 categories; a field with 25 squeezes every bar
// into ~12px of row, so the bars and their axis labels are illegible no matter
// how well the labels are cropped.
//
// The rule (per product): budget ROW_HEIGHT_PX per category row and FLOOR at the
// current 320px, so a chart with few categories renders EXACTLY as it does today
// (no regression on the charts nobody complained about).
//
// There is deliberately NO ceiling on the CHART here. An earlier pass of this
// fix capped the canvas at 720px, which was wrong and re-created the very bug
// Viktor re-opened: a 40-category field capped at 720px is ~18px per row —
// squashed and illegible all over again, with nothing to scroll to recover it.
// Capping the canvas can only ever compress rows.
//
// The 720px ceiling belongs on the CONTAINER, not the canvas: the card body
// (.demographics-chart-body, _report_tabs.scss) is `max-height: 720px;
// overflow-y: auto`, so it becomes a real scroll VIEWPORT onto a chart that
// keeps its full, legible 28px-per-row height. Past ~25 categories the card
// scrolls instead of squashing. Every category stays plotted — we deliberately
// do NOT switch to a top-N, which would hide categories outright and silently
// break the drilldown into the hidden ones.
//
// Why client-side: the bar count is unknown at render time. These are REMOTE-URL
// Chartkick charts — the partial emits the chart with its static height and the
// categories only arrive when the `demographics_chart` JSON endpoint resolves.
// So the height is applied once the Chart.js object exists, from the inline
// poller the partial already runs to patch each chart's drilldown onClick.
export const ROW_HEIGHT_PX = 28
export const MIN_HEIGHT_PX = 320

// Height in px for a chart of `categoryCount` bars: 28px per row, floored at
// 320px, UNCAPPED above (the card body scrolls — see the note above).
// Non-numeric / negative counts (chart data not resolved, malformed payload)
// fall back to the floor — never 0 / NaN, which would blank the card.
export function demographicsChartHeight(categoryCount) {
  const rows = Number.isFinite(categoryCount) && categoryCount > 0 ? categoryCount : 0
  return Math.max(MIN_HEIGHT_PX, rows * ROW_HEIGHT_PX)
}

// Sizes one Chartkick chart's element to its category count.
//
// Chartkick renders the canvas INTO `ck.element` (the div the ERB gave the
// static height), and the chart is `maintainAspectRatio: false` + `responsive`,
// so that element's height IS the chart's height — setting it and asking
// Chart.js to re-measure is the whole fix. Without the explicit resize() the
// canvas keeps its stale backing size and nothing visibly changes.
//
// This element is the chart, NOT the scroll container: it sits inside
// .demographics-chart-body, which is the thing that caps at 720px and scrolls.
// So growing it past 720px is intended — that overflow is what the body scrolls.
//
// Returns false (safe no-op) while Chartkick / the chart / the Chart.js object
// isn't ready — the partial's poller calls this on every tick during the remote
// fetch. Idempotent: re-applying the same height skips the resize, so the poll
// (150ms) and Turbo Frame re-runs don't thrash layout.
export function applyDemographicsChartHeight(chartId) {
  if (typeof window === 'undefined' || !window.Chartkick) return false
  const ck = window.Chartkick.charts[chartId]
  if (!ck || typeof ck.getChartObject !== 'function') return false
  const cjs = ck.getChartObject()
  if (!cjs) return false

  const el = ck.element || (typeof document !== 'undefined' ? document.getElementById(chartId) : null)
  if (!el) return false

  const labels = (cjs.data && cjs.data.labels) || []
  const target = demographicsChartHeight(labels.length) + 'px'
  if (el.style.height === target) return true

  el.style.height = target
  if (typeof cjs.resize === 'function') cjs.resize()
  return true
}

// Inline view <script>s cannot `import`; mirror onto window for the demographics
// partial to reach (same pattern as window.reportsCropTickLabel above and
// window.leadDrill in app/frontend/lead_drill.js).
if (typeof window !== 'undefined') {
  window.reportsDemographicsChartHeight = demographicsChartHeight
  window.reportsApplyDemographicsChartHeight = applyDemographicsChartHeight
}
